import React, { useEffect, useState } from "react";
import axios from "axios";

interface CustomerOrderHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  customerId: string | null;
}

interface OrderLineItem {
  name: string;
  qty: string;
}

interface DisplayOrder {
  orderId: string;
  date: string;
  items: OrderLineItem[];
  total: string;
  status: string;
}

const formatDate = (dateStr: string | undefined): string => {
  if (!dateStr || typeof dateStr !== "string") return "—";
  try {
    const date = new Date(dateStr);
    if (Number.isNaN(date.getTime())) return dateStr;
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return dateStr;
  }
};

const formatTotal = (o: any): string => {
  const n = o?.total ?? o?.totalAmount ?? o?.amount ?? o?.grandTotal;
  if (typeof n === "number" && !Number.isNaN(n)) return `₦${n.toLocaleString()}`;
  if (typeof n === "string" && n.trim()) return n;
  return "—";
};

const mapLineItem = (it: any): OrderLineItem => {
  const name = String(it?.name ?? it?.productName ?? it?.itemName ?? it?.title ?? "—");
  const qtyRaw = it?.quantity ?? it?.qty ?? it?.count;
  const qty = qtyRaw !== undefined && qtyRaw !== null ? String(qtyRaw) : "";
  const unit = it?.unit ?? it?.size ?? "";
  const qtyDisplay = [qty, unit].filter(Boolean).join(" ").trim();
  return { name, qty: qtyDisplay || "—" };
};

const mapOrder = (o: any, idx: number): DisplayOrder => {
  const rawId = o?.orderId ?? o?._id ?? o?.id ?? o?.orderNumber ?? idx + 1;
  const orderIdStr = typeof rawId === "string" ? rawId : String(rawId);
  const orderId = orderIdStr.startsWith("#") ? orderIdStr : `#${orderIdStr}`;

  const rawDate = o?.orderDate ?? o?.createdAt ?? o?.date ?? o?.updatedAt;
  const dateStr =
    typeof rawDate === "string"
      ? rawDate
      : typeof rawDate === "number"
        ? new Date(rawDate).toISOString()
        : rawDate instanceof Date
          ? rawDate.toISOString()
          : undefined;
  const rawItems = o?.items ?? o?.orderItems ?? o?.products ?? o?.cart ?? [];
  let items: OrderLineItem[] = Array.isArray(rawItems) ? rawItems.map(mapLineItem) : [];
  if (items.length === 0 && (o?.itemName || o?.productName)) {
    items = [mapLineItem(o)];
  }

  return {
    orderId,
    date: formatDate(dateStr),
    items: items.length ? items : [{ name: "—", qty: "—" }],
    total: formatTotal(o),
    status: String(o?.status ?? o?.orderStatus ?? "—"),
  };
};

const extractOrders = (body: any): any[] => {
  if (!body) return [];
  if (Array.isArray(body)) return body;
  if (Array.isArray(body.data)) return body.data;
  if (Array.isArray(body.orders)) return body.orders;
  if (Array.isArray(body.results)) return body.results;
  if (body.data && Array.isArray(body.data.orders)) return body.data.orders;
  if (body.data && Array.isArray(body.data.results)) return body.data.results;
  if (body.data && typeof body.data === "object" && !Array.isArray(body.data)) {
    const inner = body.data;
    if (Array.isArray(inner.orders)) return inner.orders;
    if (Array.isArray(inner.results)) return inner.results;
  }
  return [];
};

const getStatusColor = (status: string) => {
  const s = status.toLowerCase();
  switch (s) {
    case "processing":
    case "pending":
      return "bg-[#f59e0b] text-white";
    case "completed":
    case "delivered":
      return "bg-[#059669] text-white";
    case "failed":
    case "cancelled":
      return "bg-[#ef4444] text-white";
    default:
      return "bg-gray-200 text-gray-800";
  }
};

const getAuthToken = (): string => {
  const auth = sessionStorage.getItem("authState");
  const json = auth ? JSON.parse(auth) : {};
  return json?.token ?? "";
};

const CustomerOrderHistoryModal: React.FC<CustomerOrderHistoryModalProps> = ({
  isOpen,
  onClose,
  customerId,
}) => {
  const [orders, setOrders] = useState<DisplayOrder[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen || !customerId) {
      setOrders([]);
      setError(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setError(null);
      setOrders([]);
      try {
        const token = getAuthToken();
        if (!token) {
          setError("Not signed in");
          return;
        }
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/dashboard/user-order-history/${encodeURIComponent(customerId)}`,
          { headers: { Authorization: token } }
        );
        const body = res.data ?? {};
        const list = extractOrders(body);
        if (cancelled) return;
        setOrders(list.map((o, i) => mapOrder(o, i)));
      } catch (err: any) {
        if (cancelled) return;
        const message =
          err?.response?.data?.message || err?.message || "Failed to load order history";
        setError(String(message));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [isOpen, customerId]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="bg-white rounded-3xl p-6 sm:p-8 max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-xl relative"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="order-history-title"
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          aria-label="Close"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <div className="mb-6 pr-10">
          <h2 id="order-history-title" className="text-xl font-semibold text-gray-900 mb-2">
            Order History
          </h2>
          <p className="text-sm text-gray-400">
            {loading
              ? "Loading orders…"
              : error
                ? "Could not load orders."
                : `All orders placed by this customer (${orders.length})`}
          </p>
        </div>

        {loading ? (
          <p className="text-sm text-gray-500 py-8 text-center">Loading…</p>
        ) : error ? (
          <p className="text-sm text-red-500 py-8 text-center">{error}</p>
        ) : orders.length === 0 ? (
          <p className="text-sm text-gray-400 py-8 text-center">No orders found</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="py-3 font-medium text-gray-800">Order ID</th>
                  <th className="py-3 font-medium text-gray-800">Date</th>
                  <th className="py-3 font-medium text-gray-800">Items</th>
                  <th className="py-3 font-medium text-gray-800">Total</th>
                  <th className="py-3 font-medium text-gray-800">Status</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order, index) => (
                  <tr key={`${order.orderId}-${index}`} className="border-b border-gray-50 last:border-0">
                    <td className="py-4 text-gray-600 align-top">{order.orderId}</td>
                    <td className="py-4 text-gray-600 align-top">{order.date}</td>
                    <td className="py-4 text-gray-600 align-top">
                      <div className="flex flex-col gap-1">
                        {order.items.map((item, i) => (
                          <span key={i}>
                            {item.name}
                            {item.qty && item.qty !== "—" ? ` · ${item.qty}` : ""}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="py-4 text-[#008060] font-medium align-top">{order.total}</td>
                    <td className="py-4 align-top">
                      <span
                        className={`inline-block px-3 py-1.5 rounded-md text-xs font-medium capitalize ${getStatusColor(
                          order.status
                        )}`}
                      >
                        {order.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerOrderHistoryModal;
